import { type FC, type ReactNode, useMemo } from "react";
import { Button, DialogContent, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FormBuilder } from "@/shared/components/Form";
import { DialogHeader } from "@/shared/components";
import { useTagTypeQuery } from "@/partner/entities/tags";
import { getTagFormSchema, type TagFormValues } from "../model";

const FORM_ID = "tag-form";

interface TagFormProps {
  initialValues: TagFormValues;
  onSubmit: (values: TagFormValues) => Promise<void>;
  onClose: () => void;
  title: string;
  loading?: boolean;
  warning?: ReactNode;
}

export const TagForm: FC<TagFormProps> = ({
  initialValues,
  onSubmit,
  onClose,
  title,
  loading = false,
  warning,
}) => {
  const { t } = useTranslation();

  // Основная картинка типа (TagType.defaultPreviewType): у «людей» это
  // портрет, и слот 2:3 открывается первым, чтобы 16:9 не залили по
  // ошибке. Берётся по типу на момент открытия формы (из фильтра списка
  // или у редактируемого тега); переключатель слотов виден всегда.
  const { data: typeData } = useTagTypeQuery({
    variables: { id: String(initialValues.typeId) },
    skip: !initialValues.typeId,
  });
  const defaultOrientation =
    typeData?.tagType?.defaultPreviewType === "VERTICAL"
      ? "vertical"
      : typeData?.tagType?.defaultPreviewType === "HORIZONTAL"
        ? "horizontal"
        : undefined;

  const schema = useMemo(
    () =>
      getTagFormSchema(t, {
        initialTypeId: initialValues.typeId,
        defaultOrientation,
      }),
    [t, initialValues.typeId, defaultOrientation]
  );

  const handleSubmit = async (values: Record<string, unknown>) => {
    await onSubmit(values as unknown as TagFormValues);
  };

  return (
    <>
      <DialogHeader
        title={title}
        actions={
          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              type="submit"
              form={FORM_ID}
              disabled={loading}
            >
              {t("common.actions.save")}
            </Button>
          </Stack>
        }
        onClose={onClose}
        loading={loading}
      />

      <DialogContent sx={{ p: 1 }}>
        {warning}
        <FormBuilder
          formId={FORM_ID}
          schema={schema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          disabled={loading}
          submitButton={false}
          resetButton={false}
        />
      </DialogContent>
    </>
  );
};
